import { useState, useEffect, useMemo } from "react";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import {
  useFetchTopicsQuery,
  useFetchSubtopicsQuery,
  useUpdateUserTopicsMutation,
} from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditInterestsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3 pl-10",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

export function EditInterestsModal({
  open,
  onOpenChange,
  profile,
}: EditInterestsModalProps) {
  const { data: topics = [], isLoading: isLoadingTopics } = useFetchTopicsQuery(undefined, {
    skip: !open,
  });
  const { data: subtopics = [], isLoading: isLoadingSubtopics } = useFetchSubtopicsQuery(
    undefined,
    { skip: !open }
  );
  const [updateUserTopics, { isLoading: isSaving }] = useUpdateUserTopicsMutation();

  // Selected IDs (both topics and subtopics)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize selected IDs when modal opens
  useEffect(() => {
    if (open && profile.user_topics) {
      const ids = new Set<string>();
      profile.user_topics.forEach((ut) => {
        if (ut.topic_id) ids.add(ut.topic_id);
        if (ut.subtopic_id) ids.add(ut.subtopic_id);
      });
      setSelectedIds(ids);
      setSearchQuery("");
    }
  }, [open, profile.user_topics]);

  // Combined and filtered list of selectable items
  const selectableItems = useMemo(() => {
    const items: Array<{
      id: string;
      name: string;
      emoji: string | null;
      type: "topic" | "subtopic";
    }> = [];

    // Add topics
    topics.forEach((topic) => {
      items.push({
        id: topic.id,
        name: topic.name,
        emoji: topic.emoji || null,
        type: "topic",
      });
    });

    // Add subtopics
    subtopics.forEach((subtopic) => {
      items.push({
        id: subtopic.id,
        name: subtopic.name,
        emoji: subtopic.parent_topic?.emoji || null,
        type: "subtopic",
      });
    });

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return items.filter((item) => item.name.toLowerCase().includes(query));
    }

    return items;
  }, [topics, subtopics, searchQuery]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await updateUserTopics({
        userId: profile.id,
        selectedIds: Array.from(selectedIds),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update interests:", error);
    }
  };

  const isLoading = isLoadingTopics || isLoadingSubtopics;

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit interests" onClose={() => onOpenChange(false)} />

        {/* Search input in its own container */}
        <div className="flex-shrink-0 px-5 pb-4">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Icon name="search" size={16} className="text-text-secondary" />
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search interests..."
              className={inputClassName}
            />
          </div>
        </div>

        {/* Content area */}
        <SidePanelBody className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="circle" className="animate-pulse text-text-tertiary" size={24} />
            </div>
          ) : selectableItems.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-body-2 text-text-tertiary">
                {searchQuery ? "No interests found" : "No interests available"}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectableItems.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleSelection(item.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-full border transition-colors",
                      isSelected
                        ? "border-text-primary bg-text-primary/10"
                        : "border-border-tertiary hover:border-border-secondary"
                    )}
                  >
                    {item.emoji && <span className="text-base">{item.emoji}</span>}
                    <span
                      className={cn(
                        "text-body-2",
                        isSelected ? "text-text-primary" : "text-text-secondary"
                      )}
                    >
                      {item.name}
                    </span>
                    {isSelected && (
                      <Icon name="check" size={14} className="text-text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </SidePanelBody>

        <SidePanelFooter className="flex justify-between items-center">
          <span className="text-body-3 text-text-tertiary">
            {selectedIds.size} selected
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
